import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDrop, XYCoord, useDrag } from "react-dnd";
import { formElementTypes } from "../../../../../utils/Constant";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import MultiInputFields from "./multi-input-fields";
import TableCellField from "./table-cell-fields";
import { RichTextReadOnly } from "mui-tiptap";
import useExtensions from "../../../../../components/text-editor/use-extensions";
import { getElementUpdated } from "../../../../../utils/SingletonStore";

export default function SectionElement({
  index,
  element,
  section,
  onChange,
  moveCard,
  isSelected,
}: any) {
  const ref = useRef<HTMLDivElement>(null);
  const extensions = useExtensions();

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [canTakePhotos, setCanTakePhotos] = useState(false);
  const [forbidDrag, setForbidDrag] = useState(false);
  const [column1Cells, setColumn1Cells] = useState([]);
  const [column2Cells, setColumn2Cells] = useState([]);
  const [column3Cells, setColumn3Cells] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (element && element?.type === formElementTypes.table) {
      tableChildElements();
    }
    if (element && element?.type === formElementTypes.multiInput) {
      multiInputChildElements();
    }
  }, [element, tableRef?.current?.getBoundingClientRect().width]); // eslint-disable-line

  const [{ handlerId }, drop] = useDrop(() => ({
    accept: "CARD",
    collect: (monitor: any) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }

      if (item?.card) {
        // if new section is dragged then don't perform reorder
        return;
      }
      const dragIndex = item?.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, section);
      item.index = hoverIndex;
    },
  }));

  const [{ opacity }, drag] = useDrag(
    {
      type: "CARD",
      item: () => {
        return { element, index, sectionId: section?.id };
      },
      canDrag: !forbidDrag,
      collect: (monitor: any) => ({
        opacity: monitor.isDragging() ? 0.8 : 1,
      }),
    },
    [forbidDrag, element],
  );
  drag(drop(ref));

  const tableChildElements = () => {
    const filterHeader = element?.childElements
      .filter((element: any) => element?.tableElementSettings?.isHeader)
      .sort(
        (a: any, b: any) =>
          a?.tableElementSettings?.column - b?.tableElementSettings?.column,
      );
    const filterNonHeader = element?.childElements.filter(
      (element: any) => element?.tableElementSettings?.isHeader === false,
    );

    const filterAutoWidthLength = filterHeader.filter(
      (col: any) => col.tableElementSettings.headerSettings.width === 0,
    ).length;
    const filterNonAutoWidth = filterHeader.filter(
      (col: any) => col.tableElementSettings.headerSettings.width !== 0,
    );
    let nonAutoWidthSum = 0;
    filterNonAutoWidth.forEach((col: any) => {
      nonAutoWidthSum += col.tableElementSettings.headerSettings.width;
    });
    const tableWidth = tableRef?.current?.getBoundingClientRect().width;

    const columns: GridColDef[] = [];
    filterHeader.forEach((header: any, index: number) => {
      let width;
      if (tableWidth) {
        if (header?.tableElementSettings?.headerSettings?.width === 0) {
          width =
            (12 / filterAutoWidthLength / 12) *
            (tableWidth - (nonAutoWidthSum / 12) * tableWidth);
        } else {
          width =
            (header?.tableElementSettings?.headerSettings?.width / 12) *
            tableWidth;
        }
      }
      if (header?.answerOptions?.length > 0) {
        const column: GridColDef & { settings: any } = {
          field: `col${index + 1}`,
          headerName: header?.title,
          width: width,
          editable: true,
          type: "singleSelect",
          valueOptions: ["", ...header?.answerOptions],
          renderHeader: (params) => (
            <Typography
              sx={{ width: 140, fontWeight: "bold", textAlign: "center" }}
            >
              {params.colDef.headerName}
            </Typography>
          ),
          renderEditCell: (params) => (
            <Select
              sx={{ width: "100%", height: 40, padding: 1, mt: 0.75 }}
              defaultValue=""
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {header?.answerOptions.map((opt: string) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          ),
          renderCell: (params) => {
            if (params.id !== "sum") {
              return (
                <Select
                  sx={{ width: "100%", height: 40, padding: 1, mt: 0.75 }}
                  defaultValue=""
                >
                  <MenuItem value="">
                    <em>Select an option</em>
                  </MenuItem>
                  {header?.answerOptions.map((opt: string) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              );
            }
          },
          settings: {
            label: header?.title,
            inputType: header?.tableElementSettings?.headerSettings?.inputType,
            inputFormat:
              header?.tableElementSettings?.headerSettings?.inputFormat,
            column: header?.tableElementSettings?.column,
            row: header?.tableElementSettings?.row,
            isSum: header?.tableElementSettings?.headerSettings?.calculateSum,
            answerOptions: header?.answerOptions
              ? header?.answerOptions.join(", ")
              : "",
            width: header?.tableElementSettings?.headerSettings?.width,
          },
        };
        columns.push(column);
      } else {
        const column: GridColDef & { settings: any } = {
          field: `col${index + 1}`,
          headerName: header?.title,
          width: width,
          editable: false,
          type: "string",
          renderHeader: (params) => (
            <Typography
              sx={{
                width: 140,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {params.colDef.headerName}
            </Typography>
          ),
          renderEditCell: (params) => {
            if (
              header?.tableElementSettings?.headerSettings?.inputType ===
              "OpenEnd"
            ) {
              return <TableCellField header={header} />;
            }
          },
          renderCell: (params) => {
            if (params.id !== "sum") {
              if (
                header?.tableElementSettings?.headerSettings?.inputType ===
                "OpenEnd"
              ) {
                return <TableCellField header={header} />;
              }
            } else {
              if (params?.row?.fields?.includes(params.field)) {
                return (
                  <Typography
                    sx={{ display: "flex", justifyContent: "center", my: 1.5 }}
                    color="textDisabled"
                  >
                    Sum={" "}
                    <Typography sx={{ color: "rgba(0, 0, 0, 0.87)", ml: 1 }}>
                      0
                    </Typography>
                  </Typography>
                );
              }
            }
          },
          settings: {
            label: header?.title,
            inputType: header?.tableElementSettings?.headerSettings?.inputType,
            inputFormat:
              header?.tableElementSettings?.headerSettings?.inputFormat,
            column: header?.tableElementSettings?.column,
            row: header?.tableElementSettings?.row,
            isSum: header?.tableElementSettings?.headerSettings?.calculateSum,
            answerOptions: header?.answerOptions
              ? header?.answerOptions.join(", ")
              : "",
            width: header?.tableElementSettings?.headerSettings?.width,
          },
        };
        columns.push(column);
      }
    });
    setColumns(columns);

    const rows: any[] = [];
    const numberOfRows = filterNonHeader.length / filterHeader.length;
    for (let i = 1; i <= numberOfRows; i++) {
      const filterRow = element?.childElements
        .filter((element: any) => element?.tableElementSettings?.row === i)
        .sort(
          (a: any, b: any) =>
            a?.tableElementSettings?.column - b?.tableElementSettings?.column,
        );
      const updateRow: any = { id: i };
      filterRow.forEach((row: any, index: number) => {
        const filterColumn: any = columns.find(
          (col) => col.field === `col${index + 1}`,
        );
        if (filterColumn?.settings?.inputType === "TextField") {
          updateRow[`col${index + 1}`] = row?.title;
        }
      });
      rows.push(updateRow);
    }

    const sumColumns = columns.filter((header: any) => header?.settings?.isSum);
    if (sumColumns.length >= 0) {
      const fields: string[] = [];
      sumColumns.forEach((col) => {
        fields.push(col.field);
      });
      const sumRow: any = { id: "sum", fields };
      rows.push(sumRow);
    }
    setRows(rows);
  };

  const multiInputChildElements = () => {
    const filterColumn1 = element?.childElements
      .filter(
        (element: any) => element?.multiInputElementSettings?.column === 1,
      )
      .sort(
        (a: any, b: any) =>
          a?.multiInputElementSettings?.row - b?.multiInputElementSettings?.row,
      );
    const filterColumn2 = element?.childElements
      .filter(
        (element: any) => element?.multiInputElementSettings?.column === 2,
      )
      .sort(
        (a: any, b: any) =>
          a?.multiInputElementSettings?.row - b?.multiInputElementSettings?.row,
      );
    const filterColumn3 = element?.childElements
      .filter(
        (element: any) => element?.multiInputElementSettings?.column === 3,
      )
      .sort(
        (a: any, b: any) =>
          a?.multiInputElementSettings?.row - b?.multiInputElementSettings?.row,
      );
    setColumn1Cells(filterColumn1);
    setColumn2Cells(filterColumn2);
    setColumn3Cells(filterColumn3);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Accordion
        sx={{
          border: `1px solid ${isSelected ? "#E35F00" : "#E0E0E0"}`,
          boxShadow: "none",
          backgroundColor: "white",
          borderRadius: 1,
          position: "relative", // for absolute-positioned icon
          transition: "background-color 0.2s ease",

          "&:hover": {
            backgroundColor: "rgba(227, 95, 0, 0.04)", // hover background
            "& .hover-icon": {
              opacity: 1, // show icon
              transform: "translateX(0)", // slide in effect
            },
          },
        }}
        expanded={expanded}
        onChange={(e, expanded) => {
          if (getElementUpdated()) {
            setExpanded(false);
          } else {
            setExpanded(expanded);
          }
          setForbidDrag(expanded);
          onChange(e, expanded, element, section);
        }}
      >
        <AccordionSummary
          ref={ref}
          data-handler-id={handlerId}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            px: 1.4,
            m: 0,
            opacity: opacity,
          }}
          // expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${index}-content`}
          id={`panel${index}-header`}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            <Box display={"flex"} alignItems={"center"}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {element?.type === formElementTypes.textField &&
                  (element?.settings?.title || `Text Field Title`)}
                {(element?.type === formElementTypes.openEnd ||
                  element?.type === formElementTypes.singleSelect ||
                  element?.type === formElementTypes.multiSelect ||
                  element?.type === formElementTypes.matrix ||
                  element?.type === formElementTypes.table ||
                  element?.type === formElementTypes.multiInput) &&
                  (element?.settings?.title || `Question Title`)}
                {element?.type === formElementTypes.photoRequest &&
                  (element?.settings?.title || `Photo Request Title`)}
                {element?.type === formElementTypes.singleDate &&
                  (element?.settings?.title || `Date Picker Title`)}
              </Typography>
              {element?.definedName && (
                <Typography ml={2} sx={{ color: "#2E7D32", fontSize: 14 }}>
                  #{element?.definedName}
                </Typography>
              )}
            </Box>

            {element?.type === formElementTypes.photoRequest && (
              <Box alignItems={"center"} display={"flex"} sx={{ mr: 3 }}>
                <Button
                  variant="outlined"
                  sx={{ mr: 1.5, fontSize: 14, fontWeight: "510" }}
                  startIcon={<AddOutlinedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  ADD PHOTOS
                </Button>
                <CheckCircleOutlineOutlinedIcon color="success" />
              </Box>
            )}
            <Box
              className="hover-icon"
              sx={{
                position: "absolute",
                right: 10,
                top: "30%",
                transform: "translateY(-50%) translateX(10px)",
                opacity: 0,
                transition: "opacity 0.2s ease, transform 0.2s ease",
                pointerEvents: "none", // so it doesn't interfere with clicks
              }}
            >
              <DragIndicatorIcon />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {element?.type === formElementTypes.textField ? (
            <RichTextReadOnly
              content={element?.settings?.text}
              extensions={extensions}
            />
          ) : (
            <Typography>{element?.settings?.text || ""}</Typography>
          )}

          {element?.type === formElementTypes.openEnd && (
            <TextField sx={{ mt: 1 }} fullWidth placeholder="Value" disabled />
          )}

          {element?.type === formElementTypes.singleSelect && (
            <FormControl sx={{ mt: 1, width: "100%" }}>
              <RadioGroup name="radio-buttons-group">
                {element?.settings?.answerOptions.map((answer: string) => (
                  <FormControlLabel
                    key={answer}
                    value={answer}
                    control={<Radio />}
                    label={answer}
                  />
                ))}
                {element?.settings?.includeOtherOption && (
                  <FormControlLabel
                    key="other"
                    value="Other"
                    control={
                      <Radio
                        onChange={(event) =>
                          setIsOtherSelected(event.target.checked)
                        }
                      />
                    }
                    label="Other"
                  />
                )}
                {isOtherSelected && (
                  <TextField
                    sx={{ mt: 1 }}
                    fullWidth
                    placeholder="Value"
                    disabled
                  />
                )}
              </RadioGroup>
            </FormControl>
          )}

          {element?.type === formElementTypes.multiSelect && (
            <FormControl sx={{ mt: 1, width: "100%" }}>
              <FormGroup>
                {element?.settings?.answerOptions.map((answer: string) => (
                  <FormControlLabel
                    key={answer}
                    value={answer}
                    control={<Checkbox />}
                    label={answer}
                  />
                ))}
                {element?.settings?.includeOtherOption && (
                  <FormControlLabel
                    key="other"
                    value="Other"
                    control={
                      <Checkbox
                        onChange={(event) =>
                          setIsOtherSelected(event.target.checked)
                        }
                      />
                    }
                    label="Other"
                  />
                )}
                {isOtherSelected && (
                  <TextField
                    sx={{ mt: 1 }}
                    fullWidth
                    placeholder="Value"
                    disabled
                  />
                )}
              </FormGroup>
            </FormControl>
          )}

          {element?.type === formElementTypes.matrix && (
            <Box
              sx={{
                borderLeft: "1px solid #C4C4C4",
                mt: 1,
                overflowX: "scroll",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    borderTop: "1px solid #C4C4C4",
                    minWidth: 120,
                    height: 50,
                    borderBottom: "1px solid #C4C4C4",
                    borderRight: "1px solid #C4C4C4",
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                  }}
                ></Typography>
                {element?.settings?.answerOptions?.map((option: string) => (
                  <Typography
                    key={option}
                    sx={{
                      borderTop: "1px solid #C4C4C4",
                      flex: 1,
                      height: 50,
                      minWidth: 60,
                      borderBottom: "1px solid #C4C4C4",
                      borderRight: "1px solid #C4C4C4",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 1,
                      fontWeight: "600",
                    }}
                  >
                    {option}
                  </Typography>
                ))}
              </Box>
              {element?.childElements?.map((question: any, index: number) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      minWidth: 120,
                      height: 50,
                      borderBottom: "1px solid #C4C4C4",
                      borderRight: "1px solid #C4C4C4",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "600",
                        width: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {question.title}
                    </Typography>
                    {question.definedName && (
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#2E7D32",
                          width: 120,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        #{question.definedName}
                      </Typography>
                    )}
                  </Box>
                  {element?.settings?.answerOptions?.map(
                    (Option: string, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          flex: 1,
                          height: 50,
                          minWidth: 60,
                          borderBottom: "1px solid #C4C4C4",
                          borderRight: "1px solid #C4C4C4",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          p: 1,
                        }}
                      >
                        <Radio disabled />
                      </Box>
                    ),
                  )}
                </Box>
              ))}
            </Box>
          )}

          {element?.type === formElementTypes.table && (
            <DataGrid
              ref={tableRef}
              sx={{
                mt: 1,
                border: "none",
                "& .MuiDataGrid-cell": {
                  border: "1px solid rgba(224,224,224,1)",
                },
                "& .MuiDataGrid-columnHeader": {
                  border: "1px solid rgba(224,224,224,1)",
                },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-cellEmpty": {
                  all: "unset",
                  display: "none",
                },
                "& .MuiDataGrid-filler": {
                  all: "unset",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-row.Mui-selected:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-row--borderBottom": {
                  backgroundColor: "transparent !important",
                },
              }}
              rows={rows}
              columns={columns}
              isCellEditable={(params) => params.id !== "sum"}
              disableColumnResize
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              hideFooter
              slots={{
                noRowsOverlay: () => null,
              }}
            />
          )}

          {element?.type === formElementTypes.singleDate && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker sx={{ width: 300, mt: 1 }} format="LL" />
            </LocalizationProvider>
          )}

          {element?.type === formElementTypes.photoRequest && (
            <>
              <FormGroup sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(event) =>
                        setCanTakePhotos(event.target.checked)
                      }
                    />
                  }
                  label="Check if Unable to Take Photos"
                />
              </FormGroup>
              {canTakePhotos && (
                <TextField
                  sx={{ mt: 1 }}
                  fullWidth
                  placeholder="Explain"
                  disabled
                />
              )}
            </>
          )}

          {element?.type === formElementTypes.multiInput && (
            <Box display={"flex"}>
              {column1Cells.length > 0 && (
                <Box flex={1}>
                  {column1Cells.map((cell: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mt: 2,
                        width:
                          column2Cells.length > 0 ? "calc(100% - 8px)" : "100%",
                      }}
                    >
                      <MultiInputFields cell={cell} />
                    </Box>
                  ))}
                </Box>
              )}
              {column2Cells.length > 0 && (
                <Box flex={1}>
                  {column2Cells.map((cell: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mt: 2,
                        ml: 1,
                        width:
                          column3Cells.length > 0
                            ? "calc(100% - 16px)"
                            : "calc(100% - 8px)",
                      }}
                    >
                      <MultiInputFields cell={cell} />
                    </Box>
                  ))}
                </Box>
              )}
              {column3Cells.length > 0 && (
                <Box flex={1}>
                  {column3Cells.map((cell: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mt: 2,
                        ml: 1,
                        width: "calc(100% - 8px)",
                      }}
                    >
                      <MultiInputFields cell={cell} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
